import type { Theme } from '@mui/material/styles';
import { useState } from 'react';
import { formatDuration } from '../../lib/dateUtils';
import type { CreativeRow } from './data/api';

export const ELLIPSIS_SX = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const TOOLBAR_BUTTON_SX = { height: 28, px: 3, backgroundColor: 'common.white' };

export const TOOLBAR_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 44,
  mb: 3,
};

export const TOOLBAR_ICON_BUTTON_SX = (theme: Theme) => ({
  border: '1px solid',
  borderColor: theme.palette.border.default,
  borderRadius: 1,
  bgcolor: 'background.paper',
});

export const GRID_CONTAINER_SX = {
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  overflow: 'hidden',
  height: '70vh',
};

export const GRID_SX_BASE = (theme: Theme) => ({
  '--DataGrid-rowBorderColor': theme.palette.divider,
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeader': {
    borderRight: 'none',
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
    {
      outline: 'none',
    },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontSize: theme.typography.overline.fontSize,
    fontWeight: theme.typography.overline.fontWeight,
    textTransform: theme.typography.overline.textTransform,
  },
});

export const MIN_DENSITY = 0;
export const MAX_DENSITY = 100;

export const NO_CREATIVES_MESSAGE =
  'No creatives match the current filters. Widen the date range or clear a filter.';

export const TOGGLE_BUTTON_SX = {
  border: 'none',
  color: 'text.secondary',
  fontWeight: 500,
  '&:hover': { backgroundColor: 'transparent', color: 'text.primary' },
  '&.Mui-selected': {
    color: 'primary.main',
    fontWeight: 700,
    backgroundColor: 'background.paper',
    boxShadow: (theme: Theme) => theme.shadows[5],
    '&:hover': { backgroundColor: 'background.paper' },
  },
};

export type MetricsMode = 'fixed' | 'hover';

interface PagedList<T> {
  visibleItems: T[];
  hasMore: boolean;
  loadMore: () => void;
}

export function scaleWithDensity(density: number, min: number, max: number): number {
  const ratio = (density - MIN_DENSITY) / (MAX_DENSITY - MIN_DENSITY);
  return min + ratio * (max - min);
}

/** "Load more" over a client-held list, reset to `pageSize` whenever `resetKey` (the query result
 * driving `items`, by reference) changes — shared by every creatives grid's card view. */
export function usePagedList<T>(resetKey: unknown, items: T[], pageSize: number): PagedList<T> {
  const [visibleCount, setVisibleCount] = useState(pageSize);
  const [pageResetFor, setPageResetFor] = useState(resetKey);

  if (resetKey !== pageResetFor) {
    setPageResetFor(resetKey);
    setVisibleCount(pageSize);
  }

  return {
    visibleItems: items.slice(0, visibleCount),
    hasMore: visibleCount < items.length,
    loadMore: () => setVisibleCount((count) => count + pageSize),
  };
}

export function pinnedTotalsRowSx(theme: Theme, edge: 'top' | 'bottom', height: number) {
  const borderSide = edge === 'top' ? 'borderBottom' : 'borderTop';
  return {
    [`& .MuiDataGrid-pinnedRows--${edge}`]: { height: `${height}px` },
    '& .MuiDataGrid-virtualScrollerRenderZone': { marginTop: `${height}px` },
    [`& .MuiDataGrid-pinnedRows--${edge} .MuiDataGrid-row`]: {
      bgcolor: theme.palette.info.bg,
      cursor: 'default',
      height: `${height}px`,
    },
    [`& .MuiDataGrid-pinnedRows--${edge} .MuiDataGrid-cell, & .MuiDataGrid-pinnedRows--${edge} .MuiDataGrid-cell[data-field]`]:
      {
        alignItems: 'center',
        backgroundColor: theme.palette.info.bg,
        [borderSide]: '2px solid',
        borderColor: theme.palette.primary.main,
        borderRight: 0,
        display: 'flex',
        fontWeight: 700,
      },
  };
}

export function creativeSubline(creative: CreativeRow): string {
  return [creative.creativeType, creative.format, formatDuration(creative.duration)]
    .filter((part) => part !== null && part !== '')
    .join(' · ');
}

export function formatLiveDaysLabel(kind: string | null, days: number | null): string {
  const capitalized = (kind ?? 'Creative').replace(/^./, (char) => char.toUpperCase());
  return days === null ? capitalized : `${capitalized} · ${days}d live`;
}

export function deltaColor(improvement: number | null, neutralColor: string): string {
  if (improvement === null || improvement === 0) return neutralColor;
  return improvement > 0 ? 'success.main' : 'error.main';
}

export function stickyColumnSx(theme: Theme, field: string) {
  return {
    [`& .MuiDataGrid-cell[data-field="${field}"], & .MuiDataGrid-columnHeader[data-field="${field}"]`]:
      {
        backgroundColor: theme.palette.background.paper,
        boxShadow: '6px 0 6px -6px rgba(0, 0, 0, calc(var(--DataGrid-hasScrollX) * 0.18))',
        left: 0,
        position: 'sticky',
        zIndex: 2,
      },
  };
}
